import ky from 'ky';
import { retrieveAccessToken } from '@/features/auth/api';
import { authAccountKeyOf, currentAuthAccountKey } from '@/lib/authSessionGuard';
import { readSharedAuthValue } from '@/lib/sharedAuthStorage';
import {
  clearCenterViewSession,
  getItemWithExpiration,
  isCenterViewTab,
  setItemWithExpiration,
  shouldBlockCenterViewRequest,
} from '@/lib/utils';
import _ from 'lodash';

/**
 * 보낸 토큰 → 그 토큰을 보낼 때의 계정. 401 처리에서 원요청의 주인을 알기 위해 쓴다(ky 는 afterResponse 에 복제한 요청을
 * 넘겨 요청 객체로는 이을 수 없지만, 헤더는 그대로다). 토큰 몇 개뿐이라 상한만 둔다.
 */
const sentTokenAccounts = new Map<string, string>();
const rememberSentTokenAccount = (token: string, account: string | null) => {
  if (!token || !account) return;
  sentTokenAccounts.delete(token);
  sentTokenAccounts.set(token, account);
  if (sentTokenAccounts.size > 20) {
    const oldest = sentTokenAccounts.keys().next().value;
    if (oldest !== undefined) sentTokenAccounts.delete(oldest);
  }
};

const getResponseMessage = async (response: Response, fallback?: string) => {
  try {
    const bodyText = await response.clone().text();
    const bodyJson = JSON.parse(bodyText);
    return bodyJson?.message || fallback;
  } catch {
    return fallback;
  }
};

// 에러 응답 본문 전체(JSON)를 파싱. errorCode/details 같은 구조화 필드 접근용.
const getResponseBody = async (response: Response) => {
  try {
    const bodyText = await response.clone().text();
    return JSON.parse(bodyText);
  } catch {
    return null;
  }
};

const isAuthErrorResponse = async (response: Response) => {
  if (response.status === 401) return true;
  if (response.status !== 403) return false;

  const message = await getResponseMessage(response);
  return message === 'not logged in';
};

const apiClient = ky.create({
  prefixUrl: process.env.REACT_APP_SERVER,
  /**
   * ky 기본값은 timeout 10초 + GET 자동 재시도 2회다. 이 조합이 그대로 있으면
   * 느린 엔드포인트 하나가 요청 3건으로 불어나고, React Query의 재시도까지 겹치면
   * 6건이 된다 — 이미 느린 서버를 6배로 때리는 셈이라 상황이 더 나빠진다.
   * (실제로 membership-entitlements 에서 이 증상이 났다.)
   *
   * 서버 자동 재시도는 끄고, 재시도가 필요한 화면이 React Query 레벨에서
   * 횟수와 조건을 정하게 한다.
   */
  timeout: 20000,
  retry: 0,
  hooks: {
    beforeRequest: [
      async (request, options) => {
        if (shouldBlockCenterViewRequest(request.method)) {
          const message = '센터 열람 모드에서는 데이터를 변경할 수 없습니다.';
          window.alert(message);
          throw new Error(message);
        }

        // 인터넷 연결 확인 (웹)
        if (!navigator.onLine) {
          console.error('네트워크 오류: 인터넷 연결을 확인해주세요.');
          throw new Error('Network not connected');
        }
        // 이미 Authorization이 있는지(요청/옵션 양쪽 다) 확인
        const headersFromOptions = options.headers as
          | Headers
          | Record<string, string>
          | string[][]
          | undefined;
        const hasAuthInRequest = request.headers.has('authorization');
        const hasAuthInOptions =
          headersFromOptions instanceof Headers
            ? headersFromOptions.has('authorization')
            : !!headersFromOptions &&
              (Array.isArray(headersFromOptions)
                ? headersFromOptions.some(
                    ([k]) => k.toLowerCase() === 'authorization',
                  )
                : Object.keys(headersFromOptions).some(
                    k => k.toLowerCase() === 'authorization',
                  ));
        const hasCustomAuth = hasAuthInRequest || hasAuthInOptions;

        if (hasCustomAuth) {
          // 직접 지정한 토큰도 전송 시점에 지금 공유 저장소 토큰과 같으면 그 계정이 주인이다 — 같은 계정의 다른 탭이
          // 갱신한 뒤 401 이 와도 새 토큰으로 다시 보낼 수 있게 기억한다(다르면 주인을 모르므로 기억하지 않는다).
          const customToken = request.headers.get('authorization');
          if (customToken && customToken === readSharedAuthValue('authorization')) {
            rememberSentTokenAccount(customToken, currentAuthAccountKey());
          }
        }
        if (!hasCustomAuth) {
          const accessToken = getItemWithExpiration('authorization');
          if (accessToken) {
            request.headers.set('authorization', accessToken);
            // 이 토큰을 보낼 때의 계정 — 401 이 계정 전환 뒤에 도착해도 원래 주인을 알 수 있게.
            rememberSentTokenAccount(accessToken, authAccountKeyOf(getItemWithExpiration('authObject')));
          }
        }

        return request;
      },
    ],
    afterResponse: [
      async (request, options, response) => {
        // 인증 에러(401 또는 인증 관련 403)만 토큰 갱신 후 재시도
        if (await isAuthErrorResponse(response)) {
          if (isCenterViewTab()) {
            clearCenterViewSession(false);
            window.location.replace('/center-view');
            throw new Error('센터 열람 세션이 만료되었습니다.');
          }

          // 요청별 선택 옵션(추가만): noAuthRetry=true 면 토큰 갱신·같은 요청 재전송을 하지 않고 바로 실패로 돌려준다.
          // 멱등 키로 결과를 재생하는 쓰기(정산 분류 일괄 적용 등)는 로그아웃·계정 전환 뒤 옛 세션으로
          // 다시 보내지면 안 된다. 옵션이 없는 기존 호출은 아래 기존 동작 그대로다.
          if ((options as any).noAuthRetry === true) {
            const err = new Error('auth retry skipped') as Error & {
              status: number;
              authRetrySkipped: boolean;
              body?: any;
            };
            err.status = response.status;
            err.authRetrySkipped = true;
            err.body = await getResponseBody(response);
            throw err;
          }

          /*
           * 갱신·재전송의 소유자 = **원요청이 실제로 보낸 Authorization 헤더**와 그 헤더를 보낼 때의 계정(lib/authSessionGuard).
           * 401 뒤에 저장소를 읽어 소유자를 잡으면, 요청을 보낸 뒤 다른 계정이 로그인한 경우 B 를 소유자로 잡아 A 요청을
           * B 토큰으로 다시 보낸다(3회차 검증 P2). 「지금」 값은 탭이 함께 쓰는 저장소의 최신 값으로 본다(다른 탭의 전환·갱신).
           */
          const sentToken = request.headers.get('authorization');
          const sharedToken = readSharedAuthValue('authorization');
          const sharedAccount = currentAuthAccountKey();
          const requestAccount = !sentToken
            ? null
            : sentToken === sharedToken
              ? sharedAccount
              : sentTokenAccounts.get(sentToken) ?? null;

          if (!sentToken || !sharedToken || !requestAccount || requestAccount !== sharedAccount) {
            // 보낸 뒤 로그아웃·다른 계정 로그인(또는 소유자를 알 수 없음) — 갱신도 재전송도 하지 않는다.
            throw new Error('Auth retry skipped - session changed');
          }
          if (sharedToken !== sentToken) {
            // 같은 계정의 다른 요청·다른 탭이 이미 갱신했다 — 그 토큰으로 다시 보내고 저장소는 건드리지 않는다.
            request.headers.set('authorization', sharedToken);
            return ky(request);
          }

          const accessToken = sentToken;
          const refreshToken = readSharedAuthValue('refreshToken');

          if (accessToken && refreshToken) {
            try {
              // 토큰 갱신 시도
              const refreshedToken = await retrieveAccessToken(
                accessToken,
                refreshToken,
              );

              if (refreshedToken?.access.token) {
                // 갱신을 기다리는 사이 다른 계정으로 바뀌었으면 저장도 재전송도 하지 않는다. 확인과 저장 사이에 await 없음.
                const latestAccessToken = readSharedAuthValue('authorization');
                if (!latestAccessToken || currentAuthAccountKey() !== requestAccount) {
                  throw new Error('Token refresh discarded - account changed');
                }
                // 같은 계정의 다른 요청·다른 탭이 먼저 갱신했으면 그 토큰을 이어받고 덮지 않는다.
                let nextAccessToken = refreshedToken.access.token;
                if (latestAccessToken === accessToken) {
                  // 새로운 토큰을 secureLocalStorage에 저장
                  setItemWithExpiration(
                    'authorization',
                    refreshedToken.access.token,
                    refreshedToken.access.expires
                  );
                  rememberSentTokenAccount(refreshedToken.access.token, requestAccount);
                } else {
                  nextAccessToken = latestAccessToken;
                }

                // 새로운 토큰으로 헤더 업데이트
                request.headers.set(
                  'authorization',
                  nextAccessToken,
                );

                // 원래 요청 재시도
                return ky(request);
              } else {
                // 토큰 갱신 실패 시 로그아웃
                // clearAuthState();
                throw new Error('Token refresh failed - user logged out');
              }
            } catch (error) {
              // 토큰 갱신 중 오류 발생 시 로그아웃
              // clearAuthState();
              throw new Error('Token refresh error - user logged out');
            }
          } else {
            // 토큰이 없는 경우 로그아웃
            // clearAuthState();
            throw new Error('No tokens available - user logged out');
          }
        }

        // 다른 400번대, 500번대 에러 처리
        if (response.status >= 400) {
          // skipErrorAlertFor 옵션으로 특정 상태코드만 Alert 건너뛰기
          // 우선순위: top-level ky 옵션 → nested error 옵션 → 기본값(true)
          const errorOpt = (options as any).error || {};

          // throw 여부 결정: ky의 throwHttpErrors(false)를 주면 여기서도 throw 하지 않음
          const throwFromTopLevel = (options as any).throwHttpErrors;
          const throwFromNested =
            typeof errorOpt.throw === 'boolean' ? errorOpt.throw : undefined;
          const shouldThrow =
            typeof throwFromTopLevel === 'boolean'
              ? throwFromTopLevel
              : typeof throwFromNested === 'boolean'
              ? throwFromNested
              : true; // default

          // Alert 스킵 목록 병합: top-level(skipErrorAlertFor) 우선, 없으면 nested(error.skipAlertFor)
          const skipErrorAlertForTop =
            ((options as any).skipErrorAlertFor as number[]) || [];
          const skipErrorAlertForNested =
            (errorOpt.skipAlertFor as number[]) || [];
          const skipErrorAlertFor =
            skipErrorAlertForTop.length > 0
              ? skipErrorAlertForTop
              : skipErrorAlertForNested;

          let errorMessage = '오류가 발생했습니다.';

          switch (response.status) {
            case 400:
              errorMessage = '잘못된 요청입니다.';
              break;
            case 404:
              errorMessage = '요청한 리소스를 찾을 수 없습니다.';
              break;
            case 412:
              errorMessage =
                '센터에서 수강권을 관리하고 있습니다\n\n센터에 문의해주세요';
              break;
            case 500:
              errorMessage = '서버 내부 오류가 발생했습니다.';
              break;
          }

          // 서버가 반환한 message 필드가 있으면 그걸 우선 사용
          const serverMessage = await getResponseMessage(response, errorMessage);

          // throw 정책에 따라 분기: false면 throw 하지 않고 Response 반환(호출부에서 status로 분기)
          if (shouldThrow === false) {
            return response;
          }

          const serverBody = await getResponseBody(response);
          const err = new Error(serverMessage) as Error & {
            status: number;
            serverMessage: string;
            body?: any;
          };
          err.status = response.status;
          err.serverMessage = serverMessage;
          err.body = serverBody;
          throw err;
        }

        return response;
      },
    ],
  },
});

export default apiClient;

export async function safeJson(res: Response) {
  try {
    const text = await res.text();

    if (_.isEmpty(text)) return null; // 빈 문자열이면 null

    const parsed = _.attempt(JSON.parse, text);

    if (_.isError(parsed)) return null; // JSON 파싱 실패
    if (!_.isPlainObject(parsed) && !Array.isArray(parsed)) return null; // 객체/배열 아니면 무시

    return parsed;
  } catch {
    return null; // 네트워크 단절 등
  }
}
